import { useMemo, useState } from 'react'
import { Kopfleiste, Seitenkopf } from './Kopfleiste.js'
import { Branchenschau, type SchauStueck } from './Branchenschau.js'
import { Fuss } from './Fuss.js'
import { Paketleiste } from './Paketleiste.js'
import { useAuftritt, useBreit, useZaehler, zeigerLicht } from './bewegung.js'
import { INDUSTRIES, INDUSTRY_LABELS, START_PACKAGES, type Industry } from '@raporto/shared'

/**
 * Die Branchenseite.
 *
 * Raporto ist ein Produkt, nicht fünfzehn. Die Branche steuert genau eine
 * Sache: welcher Leistungskatalog beim Einrichten vorgeschlagen wird.
 *
 * ── Warum die Schau bedienbar ist und nicht bloss hübsch ─────────────────
 *
 * Ein Karussell, das sich dreht und sonst nichts tut, ist eine Discokugel.
 * Dieses hier ist die Auswahl: fünfzehn Gewerbe, eines gross in der Mitte mit
 * seinem Bild, die Nachbarn als schmale Streifen daneben – und wer seines
 * anklickt, sieht darunter **seinen vollständigen Katalog** –
 * jede Position mit Nummer, Bezeichnung und Einheit, genau so, wie sie nach
 * dem Einrichten dasteht. Die Bewegung ist der Weg zur Information, nicht ihr
 * Ersatz.
 *
 * Das ist auch «weniger ist mehr»: vorher standen hier fünfzehn Karten
 * gleichzeitig, jede mit drei Beispielzeilen – 2790 Pixel, die fünfzehnmal
 * dasselbe sagten. Jetzt steht **ein** Katalog da, dafür ganz.
 *
 * Vorher stand hier der Gewerbekreis – dieselbe Darstellung, die jetzt den
 * Kopf der Funktionenseite trägt. Er sagte zu jedem Gewerbe dasselbe wie
 * heute, aber er zeigte nichts: fünfzehn gleich aussehende Kästchen auf einer
 * Ellipse. Ein Gewerbe erkennt man an seinem Material, nicht an seiner
 * Schriftart.
 *
 * Die Schau hält an, sobald Zeiger oder Fokus da sind – wer liest, wird nicht
 * weitergeschoben. Bedienen lässt sie sich auf vier Wegen: Vorschaukarte,
 * Pfeile, Punkte, Pfeiltasten. Für Tastatur und Handy steht dieselbe Auswahl
 * noch einmal als flache Leiste darunter; eine Bedienung, die nur mit der
 * Maus funktioniert, ist keine.
 *
 * Alle Zahlen und Zeilen kommen aus `packages/shared/src/industries.ts`,
 * derselben Quelle, aus der die Anwendung ihre Kataloge lädt. Kommt ein
 * Gewerbe dazu, erscheint es von selbst – im Kreis, in der Leiste und in den
 * Zahlen. Eine gepflegte Zweitliste wäre ab der ersten Änderung eine Lüge.
 */

/*
  Die Branchenbilder.

  Eine Datei je Gewerbe, benannt nach dem Branchenschlüssel, als Ordner
  eingelesen. Ein neues Bild wirkt ohne Codeänderung – Datei ablegen, fertig.

  Fünfzehn echte Aufnahmen, fertig gebaute Räume und Werkstücke, keine
  Menschen und keine Gesichter. Einheitlich beschnitten auf 900 mal 1200 und
  als JPEG gespeichert; zusammen 2,6 Megabyte statt 35 als PNG.

  `.png` steht bewusst NICHT im Muster: die Ausgangsdateien liegen noch im
  selben Ordner, und eingelesen würden sie mitgebündelt – fünfunddreissig
  Megabyte, die niemand herunterlädt.
*/
const BILDER = import.meta.glob('../medien/branchen/*.{jpg,jpeg,webp}', {
  eager: true,
  query: '?url',
  import: 'default'
}) as Record<string, string>

function bildVon(branche: Industry): string {
  const treffer = Object.entries(BILDER).filter(
    ([pfad]) => pfad.split('/').pop()?.replace(/\.[a-z]+$/i, '') === branche
  )
  /* Ein Foto schlägt die gezeichnete Vorlage. So genügt es, die Bilddatei
     dazuzulegen – der Platzhalter muss nicht erst gelöscht werden, und
     solange eine Branche noch keines hat, bleibt ihr Platzhalter stehen. */
  const foto = treffer.find(([pfad]) => !pfad.endsWith('.svg'))
  return (foto ?? treffer[0])?.[1] ?? ''
}

const GEWERBE = INDUSTRIES.filter((b) => b !== 'andere')

function paketVon(branche: Industry) {
  return START_PACKAGES.find((p) => p.industry === branche)
}

function zeilen(branche: Industry) {
  return paketVon(branche)?.items ?? []
}

const POSITIONEN_GESAMT = GEWERBE.reduce((summe, b) => summe + zeilen(b).length, 0)

export function Branchen({
  angemeldet,
  aufAnmelden,
  aufStarten,
  aufStart,
  aufPreise,
  aufPfad,
}: {
  angemeldet: boolean
  aufAnmelden: () => void
  aufStarten: () => void
  aufStart: () => void
  aufPreise: () => void
  aufPfad: (pfad: string) => void
}): JSX.Element {
  const weiter = aufAnmelden
  const breit = useBreit()
  const [gewaehlt, setGewaehlt] = useState<Industry>('boden-parkett')
  const [zahlGewerbe, refGewerbe] = useZaehler<HTMLParagraphElement>(GEWERBE.length)
  const [zahlPositionen, refPositionen] = useZaehler<HTMLParagraphElement>(POSITIONEN_GESAMT)
  const kapitel = useAuftritt<HTMLDivElement>()
  const schlussAuf = useAuftritt<HTMLDivElement>()

  /*
    Die Stücke des Kreises. Sie kommen aus derselben Quelle wie die Kataloge –
    Gewerbe, Zahl der Positionen, erste Zeile aus dem Startpaket. Eine
    erfundene Beschreibung wäre die erste Lüge, die beim Einrichten auffliegt.
  */
  const stuecke = useMemo<SchauStueck[]>(
    () =>
      GEWERBE.map((branche) => {
        const z = zeilen(branche)
        return {
          id: branche,
          titel: INDUSTRY_LABELS[branche],
          marke: `${z.length} Positionen`,
          text: z.length
            ? `Beginnt mit «${z[0].name}» – ${z.length} Zeilen im Startkatalog.`
            : 'Leerer Startkatalog.',
          bild: bildVon(branche)
        }
      }),
    []
  )

  const katalog = zeilen(gewaehlt)

  return (
    <div className="rp-pg rp-glasseite">
      <Kopfleiste
        hier="branchen"
        angemeldet={angemeldet}
        aufStart={aufStart}
        aufPfad={aufPfad}
        aufAnmelden={aufAnmelden}
        aufStarten={aufStarten}
      />

      <Seitenkopf
        augenbraue="Branchen"
        titel="Dein Startkatalog"
        hell="steht schon."
        vorspann="Ein Malerbetrieb bekommt dieselbe Software wie ein Parkettleger, nur mit anderem Inhalt. Gewerbe wählen – oben in der Schau oder in der Leiste darunter – und der Katalog steht da, Zeile für Zeile."
      />

      {/*
        Die Branchenschau.

        Eine grosse Karte in der Mitte mit dem Bild des Gewerbes, seitlich
        schmale Streifen der Nachbarn. Sie ist nicht Dekoration: die Karte in
        der Mitte bestimmt, welcher Katalog weiter unten steht. Wer eine
        Vorschau anklickt, holt sie in die Mitte, und die Tabelle wechselt
        mit.

        Bedienbar bleibt sie ausserdem über die flache Leiste weiter unten –
        dieselbe Auswahl, für Tastatur, Handy und Vorlesesoftware.
      */}
      <section className="rp-pg-kopf rp-pg-kreis">
        <Branchenschau
          stuecke={stuecke}
          aktiv={Math.max(0, GEWERBE.findIndex((b) => b === gewaehlt))}
          aufAktiv={(i) => setGewaehlt(GEWERBE[i])}
        />
      </section>

      <section className="rp-pg-kopf rp-pg-kopf-knoepfe">
        <div className="rp-pg-zahlen">
          <p className="rp-pg-zahl" ref={refGewerbe}>
            <b>{zahlGewerbe}</b>
            <span>Gewerbe mit eigenem Startkatalog</span>
          </p>
          <p className="rp-pg-zahl" ref={refPositionen}>
            <b>{zahlPositionen}</b>
            <span>vorbereitete Katalogpositionen</span>
          </p>
          <p className="rp-pg-zahl">
            <b>1</b>
            <span>Produkt dahinter, nicht fünfzehn</span>
          </p>
        </div>
      </section>

      <section className="rp-pg-abschnitt" id="branchenraster">
        <div className="rp-pg-kapitel" ref={kapitel}>
          <p className="rp-pg-augenbraue">Die Kataloge</p>
          <h2>Was am ersten Tag schon drinsteht.</h2>
          <p>
            Unverändert aus der Quelle, aus der die Anwendung lädt. Jede Zeile lässt sich ändern,
            löschen oder ersetzen; überschrieben wird nie etwas.
          </p>
        </div>

        {/*
          Dieselbe Auswahl noch einmal flach. Der Kreis ist der schöne Weg, diese
          Leiste der verlässliche: Tastatur, Handy, Vorlesesoftware. Fünfzehn
          Namen nebeneinander sind ausserdem schneller zu überfliegen als
          fünfzehn Karten nacheinander.
        */}
        <div className="rp-br-leiste" role="tablist" aria-label="Gewerbe wählen">
          {GEWERBE.map((branche) => (
            <button
              type="button"
              role="tab"
              key={branche}
              className="rp-br-reiter"
              aria-selected={branche === gewaehlt}
              aria-controls="katalogtafel"
              onClick={() => setGewaehlt(branche)}
            >
              {INDUSTRY_LABELS[branche]}
              <span>{zeilen(branche).length}</span>
            </button>
          ))}
        </div>

        <div
          className="rp-br-tafel"
          id="katalogtafel"
          role="tabpanel"
          aria-live="polite"
          onMouseMove={zeigerLicht}
        >
          <span className="rp-pg-licht" aria-hidden="true" />
          <div className="rp-br-tafelkopf">
            <h3>{INDUSTRY_LABELS[gewaehlt]}</h3>
            <span className="rp-br-anzahl">{katalog.length} Positionen</span>
          </div>
          <table className="rp-fn-tabelle">
            <tbody>
              {katalog.map((z) => (
                <tr key={z.code}>
                  <td className="rp-fn-nr">{z.code}</td>
                  <td>{z.name}</td>
                  <td className="rp-fn-einheit">{z.unit}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="rp-br-fuss">
          Ansätze stehen fast überall auf null: ein geschätzter Preis landet sonst ungeprüft auf
          einer echten Offerte. Bezeichnung und Einheit stimmen, der Ansatz gehört dem Betrieb.
          {breit ? ' Boden und Parkett tragen Preise – sie stammen aus dem Bestand eines echten Betriebs.' : ''}
        </p>
      </section>

      {/*
        Die Kita-Gemeinschaft – nur auf der Kita-Tafel.

        Sie steht hier und nicht auf der Preisseite: Es ist kein Tarif,
        sondern eine Organisationsform. Die Preise bleiben dieselben, und
        genau das muss dastehen – sonst rechnet jemand mit einem Zuschlag,
        den es nicht gibt.

        Der Einstieg ist derselbe Knopf wie für jeden anderen Betrieb. Die
        Gemeinschaft richtet die erste Inhaberin danach in ihrem Bereich ein
        und lädt die anderen ein.
      */}
      {gewaehlt === 'kinderbetreuung' && (
        <section className="rp-pg-abschnitt" id="kita-gemeinschaft">
          <h2>Eine Kita. Mehrere Inhaberinnen. Jeder Bereich für sich.</h2>
          <p>
            Ihr führt eine Kita gemeinsam, aber jede Inhaberin betreut ihre eigenen
            Kinder und stellt ihre Rechnungen selbst? Mit Raporto organisiert ihr eure
            Kita unter einem gemeinsamen Namen. Jede Inhaberin arbeitet in ihrem eigenen
            geschützten Bereich und wählt das passende Abo.
          </p>
          <table className="rp-fn-tabelle" id="kita-gemeinschaft-tafel">
            <tbody>
              <tr>
                <td>Gemeinsam</td>
                <td>Kita-Name, Logo, Standortadresse – im Briefkopf jeder Rechnung</td>
              </tr>
              <tr>
                <td>Je Inhaberin</td>
                <td>
                  Kinder, Eltern, Betreuungsverträge, Rechnungen, IBAN,
                  Ansprechperson, Mitarbeitende, eigenes Abo
                </td>
              </tr>
              <tr>
                <td>Mitarbeiterin</td>
                <td>
                  Arbeitet in einem bestehenden Bereich. Kein eigener Bereich, kein
                  zusätzliches Abo – im Rahmen des gebuchten Pakets.
                </td>
              </tr>
              <tr>
                <td>Inhaberin</td>
                <td>
                  Eigener geschützter Bereich, eigenes Abo, eigene Rechnungen und eigene
                  IBAN.
                </td>
              </tr>
            </tbody>
          </table>
          <p className="rp-br-fuss">
            Ab CHF 19.90 pro Inhaberin und Monat – dieselben Pakete Basic, Pro und
            Premium wie für jeden anderen Betrieb, ohne Zuschlag und ohne Sonderpreis.
            Die Geschäftsdaten der Bereiche bleiben getrennt; eine gemeinsame Marke
            führt keine Buchhaltung zusammen.
          </p>
        </section>
      )}

      <Paketleiste aufPreise={aufPreise} />

      <section className="rp-pg-schluss">
        <div className="rp-pg-schlusskasten" ref={schlussAuf}>
          <span className="rp-strahl" aria-hidden="true">
            <span />
          </span>
          <h2>Gewerbe nicht aufgeführt.</h2>
          <p>
            Dann beginnt der Betrieb mit einem leeren Katalog. Der Funktionsumfang ist derselbe –
            die Branche steuert ausschliesslich den Vorschlag beim Einrichten.
          </p>
          <div className="rp-pg-aktionen">
            <button className="rp-pg-knopf rp-pg-knopf-hell" type="button" onClick={weiter}>
              {angemeldet ? 'Zur App' : 'Kostenlos starten'}
            </button>
            <button className="rp-pg-knopf rp-pg-knopf-glas" type="button" onClick={aufPreise}>
              Preise vergleichen
            </button>
          </div>
        </div>
      </section>


      <Fuss aufPfad={aufPfad} />
    </div>
  )
}
